<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const tableData = computed(() => [
    {
        label: t('landingPage.v2.comparison.table.rows[0].label'),
        classical: t('landingPage.v2.comparison.table.rows[0].classical'),
        cyberalarm: t('landingPage.v2.comparison.table.rows[0].cyberalarm'),
        highlight: true
    },
    {
        label: t('landingPage.v2.comparison.table.rows[1].label'),
        classical: t('landingPage.v2.comparison.table.rows[1].classical'),
        cyberalarm: t('landingPage.v2.comparison.table.rows[1].cyberalarm'),
        highlight: true
    },
    {
        label: t('landingPage.v2.comparison.table.rows[2].label'),
        classical: t('landingPage.v2.comparison.table.rows[2].classical'),
        cyberalarm: t('landingPage.v2.comparison.table.rows[2].cyberalarm'),
        highlight: true
    },
    {
        label: t('landingPage.v2.comparison.table.rows[3].label'),
        classical: t('landingPage.v2.comparison.table.rows[3].classical'),
        cyberalarm: t('landingPage.v2.comparison.table.rows[3].cyberalarm'),
        highlight: true
    },
    {
        label: t('landingPage.v2.comparison.table.rows[4].label'),
        classical: t('landingPage.v2.comparison.table.rows[4].classical'),
        cyberalarm: t('landingPage.v2.comparison.table.rows[4].cyberalarm'),
        highlight: true
    },
    {
        label: t('landingPage.v2.comparison.table.rows[5].label'),
        classical: t('landingPage.v2.comparison.table.rows[5].classical'),
        cyberalarm: t('landingPage.v2.comparison.table.rows[5].cyberalarm'),
        highlight: true
    },
  {
    label: t('landingPage.v2.comparison.table.rows[6].label'),
    classical: t('landingPage.v2.comparison.table.rows[6].classical'),
    cyberalarm: t('landingPage.v2.comparison.table.rows[6].cyberalarm'),
    highlight: true
  },
  {
    label: t('landingPage.v2.comparison.table.rows[7].label'),
    classical: t('landingPage.v2.comparison.table.rows[7].classical'),
    cyberalarm: t('landingPage.v2.comparison.table.rows[7].cyberalarm'),
    highlight: true
  },
]);
</script>

<template>
    <!-- Mobile View (Cards) -->
    <div class="mx-auto mt-12 max-w-md space-y-8 lg:hidden">
        <!-- Classical Trainings Card -->
        <section class="rounded-3xl bg-gray-900 border border-white/5 p-8">
            <h3 class="text-lg font-bold text-pink-500 uppercase tracking-wider mb-6 text-center"
                v-html="$t('landingPage.v2.comparison.table.headers.classical')">
            </h3>
            <ul role="list" class="space-y-4">
                <li v-for="(row, index) in tableData" :key="index"
                    class="flex flex-col space-y-2 border-b border-white/5 pb-4 last:border-0 relative">
                    <!-- Label -->
                    <span class="text-gray-200 font-medium text-sm">{{ row.label }}</span>
                    <!-- Value -->
                    <div class="flex items-center text-gray-400">
                        <span v-if="row.classical === '-'" class="text-gray-600">—</span>
                        <span v-else>{{ row.classical }}</span>
                    </div>
                </li>
            </ul>
        </section>

        <!-- Cyberalarm Card -->
        <section class="rounded-3xl bg-gray-900 border border-terciary/30 p-8 relative overflow-hidden">
            <!-- Background Glow -->
            <div class="absolute inset-0 bg-terciary/5 pointer-events-none"></div>
            <div
                class="absolute top-0 right-0 -mr-16 -mt-16 w-32 h-32 bg-terciary/20 blur-3xl rounded-full pointer-events-none">
            </div>

            <h3 class="text-lg font-bold text-teal-400 uppercase tracking-wider mb-6 text-center relative">{{
                $t('landingPage.v2.comparison.table.headers.cyberalarm') }}
            </h3>
            <ul role="list" class="space-y-4 relative">
                <li v-for="(row, index) in tableData" :key="index"
                    class="flex flex-col space-y-2 border-b border-terciary/20 pb-4 last:border-0">
                    <!-- Label -->
                    <span class="text-white font-medium text-sm">{{ row.label }}</span>
                    <!-- Value -->
                    <div class="flex items-center font-bold" :class="row.highlight ? 'text-terciary' : 'text-white'">
                        <svg v-if="row.cyberalarm === 'YES'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                            fill="currentColor" class="w-6 h-6 text-teal-400">
                            <path fill-rule="evenodd"
                                d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                                clip-rule="evenodd" />
                        </svg>
                        <span v-else>{{ row.cyberalarm }}</span>
                    </div>
                </li>
            </ul>
        </section>
    </div>

    <!-- Desktop View (Table) -->
    <div class="hidden lg:block relative mt-20 bg-gray-900 border border-white/5 rounded-3xl p-8 overflow-hidden">

        <!-- Table Grid -->
        <div class="grid grid-cols-12 relative z-10">

            <!-- Cyberalarm Glow Box Background (Absolute placement behind grid) -->
            <!-- Positioned to cover the last 4 columns (Cyberalarm) -->
            <div
                class="absolute top-0 bottom-0 right-0 w-1/3 border border-terciary/30 bg-terciary/5 rounded-xl shadow-[0_0_30px_rgba(20,184,166,0.1)] pointer-events-none -z-10">
            </div>

            <!-- Headers -->
            <div class="contents">
                <div class="col-span-5 pb-6"></div> <!-- Spacer for Label col -->
                <div class="col-span-3 pb-6 text-center">
                    <h4
                        class="text-pink-500 font-bold uppercase tracking-wider text-lg border-b border-white/10 pb-4 inline-block w-full">
                        <span v-html="$t('landingPage.v2.comparison.table.headers.classical')"></span>
                    </h4>
                </div>
                <div class="col-span-4 pb-6 text-center relative">
                    <h4
                        class="text-terciary font-bold uppercase tracking-wider text-lg border-b border-terciary/30 pb-4 inline-block w-full">
                        {{ $t('landingPage.v2.comparison.table.headers.cyberalarm') }}</h4>
                </div>
            </div>

            <!-- Data Rows -->
            <template v-for="(row, index) in tableData" :key="index">
                <!-- Label -->
                <div
                    class="col-span-5 py-6 flex items-center text-gray-300 font-medium border-b border-white/5 text-lg">
                    {{ row.label }}
                </div>

                <!-- Classical -->
                <div class="col-span-3 py-6 flex items-center justify-center text-gray-400 border-b border-white/5"
                    :class="{ 'border-b-0': index === tableData.length - 1 }">
                    {{ row.classical }}
                </div>

                <!-- Cyberalarm -->
                <div class="col-span-4 py-6 flex items-center justify-center text-white font-bold relative border-b border-terciary/30"
                    :class="{ 'border-b-0': index === tableData.length - 1 }">
                    <span :class="{ 'text-terciary': row.highlight }">
                        <svg v-if="row.cyberalarm === 'YES'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                            fill="currentColor" class="w-8 h-8 text-terciary mx-auto">
                            <path fill-rule="evenodd"
                                d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                                clip-rule="evenodd" />
                        </svg>
                        <span v-else>{{ row.cyberalarm }}</span>
                    </span>
                </div>
            </template>

        </div>
    </div>
</template>

<style scoped></style>
